import {
  ChevronDownIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

import {
  getPoliceReportStatusConfig,
  getQuickStatusActions,
  getStatusOptions,
  type PoliceReportAttestationStatus,
} from '@/components/police/utils/policeUtils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { PoliceStatusBadge } from '../PoliceStatusBadge';

interface PoliceStatusSelectorProps {
  currentStatus?: PoliceReportAttestationStatus | null;
  onStatusChange: (status: PoliceReportAttestationStatus) => void;
  disabled?: boolean;
  showQuickActions?: boolean;
  size?: 'sm' | 'default' | 'lg';
}

export const PoliceStatusSelector = ({
  currentStatus,
  onStatusChange,
  disabled = false,
  showQuickActions = true,
  size = 'default',
}: PoliceStatusSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentConfig = getPoliceReportStatusConfig(currentStatus);
  const quickActions = getQuickStatusActions(currentStatus);
  const allOptions = getStatusOptions();

  const handleStatusChange = (status: PoliceReportAttestationStatus) => {
    onStatusChange(status);
    setIsOpen(false);
  };

  // Quick action buttons for common status transitions
  if (showQuickActions && quickActions.length > 0 && !disabled) {
    return (
      <div className="flex items-center justify-between">
        {/* Actions */}
        <div className="flex items-center space-x-2">
          {quickActions.map((action) => (
            <Button
              key={action.status}
              variant={
                action.variant === 'destructive' ? 'outline' : action.variant
              }
              size="sm"
              onClick={() => handleStatusChange(action.status)}
            >
              {action.label}
            </Button>
          ))}

          <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="px-2">
                <EllipsisHorizontalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>Change Status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {allOptions.map((option) => {
                const isSelected = option.value === currentStatus;
                return (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => handleStatusChange(option.value)}
                    className={`flex-col items-start space-y-1 py-3 ${isSelected ? 'bg-accent' : ''}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center space-x-2">
                        <PoliceStatusBadge status={option.value} size="sm" />
                        <span>{option.label}</span>
                      </div>
                      {isSelected && (
                        <span className="text-xs text-muted-foreground">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {option.description}
                    </p>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  }

  // Fallback to dropdown-only selector
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={size}
          disabled={disabled}
          className="justify-between w-full"
        >
          <PoliceStatusBadge status={currentStatus} />
          <ChevronDownIcon className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72">
        <DropdownMenuLabel>Select Status</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {allOptions.map((option) => {
          const isSelected = option.value === currentStatus;
          return (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleStatusChange(option.value)}
              className={`flex-col items-start space-y-1 py-3 ${isSelected ? 'bg-accent' : ''}`}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-2">
                  <PoliceStatusBadge status={option.value} size="sm" />
                  <span>{option.label}</span>
                </div>
                {isSelected && (
                  <span className="text-xs text-muted-foreground">Current</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {option.description}
              </p>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
